import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, newIdempotencyKey } from './api';
import { useSession } from './session';
import type {
  AuditRow, Booking, CompletedOrder, DashboardData, CustomerDetail, CustomerListItem, FloorCar, FloorDetail,
  FloorStatus, Localized, MyJob, OrderDetail, OrderListItem,
  OrderQuote, OrderSummary, PermissionGroup, QrResolution, RefundResult, RoleRow,
  Service, SettingRow, StaffMember, TierRow, Vehicle, WorkStatus,
  Inspection, InspectionPhoto, VehicleHistory, CalendarData,
} from './types';

export function useCustomers(search: string, status?: string) {
  const params = new URLSearchParams();
  if (search.trim()) params.set('q', search.trim());
  if (status) params.set('status', status);

  return useQuery({
    queryKey: ['customers', search.trim(), status ?? ''],
    queryFn: () => api<{ items: CustomerListItem[]; nextCursor: string | null }>(
      `/admin/customers?${params.toString()}`,
    ),
    placeholderData: keepPreviousData,
  });
}

export const useCustomer = (id: string | undefined) =>
  useQuery({
    queryKey: ['customer', id],
    enabled: Boolean(id),
    queryFn: () => api<CustomerDetail>(`/admin/customers/${id}`),
  });

export const useCustomerOrders = (id: string | undefined) =>
  useQuery({
    queryKey: ['customer', id, 'orders'],
    enabled: Boolean(id),
    queryFn: () => api<{ items: OrderSummary[] }>(`/admin/customers/${id}/orders`),
  });

export const useServices = () =>
  useQuery({
    queryKey: ['services'],
    staleTime: 5 * 60_000,
    queryFn: () => api<{ items: Service[] }>('/admin/services'),
  });

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ id: string; loyaltyId: string; qrToken: string }>('/admin/customers', {
        method: 'POST', body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export function useAddVehicle(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Vehicle>(`/admin/customers/${customerId}/vehicles`, { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer', customerId] }),
  });
}

export function useUpdateVehicle(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<Vehicle>(`/admin/vehicles/${id}`, { method: 'PATCH', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer', customerId] }),
  });
}

export function useArchiveVehicle(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/admin/vehicles/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer', customerId] }),
  });
}

export const useIssueAccessLink = (customerId: string) =>
  useMutation({
    mutationFn: () =>
      api<{ url: string; expiresInMinutes: number }>(
        `/admin/customers/${customerId}/access-link`, { method: 'POST' },
      ),
  });

/**
 * The running total of an order being typed.
 *
 * A query, not a mutation, because the answer belongs to one exact basket.
 * As a mutation every keystroke fired its own request and whichever reply
 * landed last won — so a total could settle on an older basket than the one
 * on screen, and the cashier read a figure that no longer matched the lines.
 * Keyed by the basket, react-query drops replies to baskets that have moved
 * on and keeps the previous total visible while the next one is in flight.
 */
export const useQuote = (payload: unknown, enabled: boolean) =>
  useQuery({
    queryKey: ['quote', JSON.stringify(payload)],
    enabled,
    placeholderData: keepPreviousData,
    retry: false,
    // Prices are decided here and nowhere else, so a cached answer is as good
    // as a fresh one for the same basket.
    staleTime: 30_000,
    queryFn: () => api<OrderQuote>('/admin/orders/quote', { method: 'POST', body: payload }),
  });

export function useCompleteOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<CompletedOrder>('/admin/orders/complete', {
        method: 'POST',
        body,
        // Generated once per attempt, so a network retry cannot post twice.
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
    },
  });
}

export interface Followup {
  id: string;
  dueAt: string;
  overdue: boolean;
  customerId: string;
  customerName: string;
  phone: string;
  loyaltyId: string;
  preferredLanguage: string;
  serviceName: Localized;
  vehicle: string | null;
}

export const useFollowups = (withinDays: number) =>
  useQuery({
    queryKey: ['followups', withinDays],
    queryFn: () => api<{ items: Followup[]; totals: { dueNow: number; upcoming: number } }>(
      `/admin/followups?withinDays=${withinDays}`,
    ),
  });

export function useDismissFollowup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ dismissed: boolean }>(`/admin/followups/${id}/dismiss`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['followups'] }),
  });
}

export interface WalletPass {
  id: string;
  provider: 'APPLE' | 'GOOGLE';
  status: 'NOT_ISSUED' | 'ISSUED' | 'ACTIVE' | 'ERROR' | 'REVOKED';
  externalId: string;
  version: number;
  issuedAt: string | null;
  lastSyncedAt: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
  revokedAt: string | null;
  customerId: string;
  customerName: string;
  loyaltyId: string;
}

interface WalletPassList {
  items: WalletPass[];
  totals: { notIssued: number; live: number; failing: number; revoked: number };
  providers: { apple: string; google: string };
}

export const useWalletPasses = (status: string) =>
  useQuery({
    queryKey: ['wallet-passes', status],
    queryFn: () => api<WalletPassList>(
      `/admin/wallet/passes${status ? `?status=${status}` : ''}`,
    ),
    // Only poll while something is still being issued. A settled list would
    // otherwise keep asking all day on a screen left open at the counter.
    refetchInterval: (query) => (query.state.data?.totals.notIssued ? 5_000 : false),
  });

export function useIssueWalletPass(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (providers: Array<'APPLE' | 'GOOGLE'>) =>
      api<{ requested: number }>(`/admin/customers/${customerId}/wallet/issue`, {
        method: 'POST',
        body: { providers },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet-passes'] });
      queryClient.invalidateQueries({ queryKey: ['customer-passes', customerId] });
    },
  });
}

export const useCustomerPasses = (customerId: string) =>
  useQuery({
    queryKey: ['customer-passes', customerId],
    // Filtered by the server: pulling the whole estate to pick two rows would
    // get slower with every pass ever issued.
    queryFn: () => api<WalletPassList>(`/admin/wallet/passes?customerId=${customerId}`),
    select: (data) => data.items,
    refetchInterval: (query) => (
      query.state.data?.items.some((pass) => pass.status === 'NOT_ISSUED') ? 4_000 : false
    ),
  });

export function useResyncPass() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ queued: boolean }>(`/admin/wallet/passes/${id}/resync`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wallet-passes'] }),
  });
}

export function useRevokePass() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<{ revoked: boolean }>(`/admin/wallet/passes/${id}/revoke`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet-passes'] });
      queryClient.invalidateQueries({ queryKey: ['customer-passes'] });
    },
  });
}

export interface FleetVehicle {
  id: string;
  make: string;
  model: string;
  year: number | null;
  color: string | null;
  licensePlate: string | null;
  vin: string | null;
  archivedAt: string | null;
  customerId: string;
  ownerName: string;
  loyaltyId: string;
  lastServicedAt: string | null;
  orders: number;
}

export const useVehicleFleet = (search: string, includeArchived: boolean) =>
  useQuery({
    queryKey: ['vehicles', search, includeArchived],
    queryFn: () => api<{ items: FleetVehicle[] }>(
      `/admin/vehicles?${new URLSearchParams({
        ...(search ? { search } : {}),
        ...(includeArchived ? { includeArchived: 'true' } : {}),
      })}`,
    ),
  });

export interface DraftSummary {
  id: string;
  customerId: string;
  customerName: string;
  loyaltyId: string;
  lineCount: number;
  subtotalTetri: number;
  discountBp: number;
  notes: string | null;
  updatedAt: string;
}

export interface DraftDetail {
  id: string;
  customerId: string;
  vehicleId: string | null;
  lines: Array<{
    serviceId: string;
    quantity: number;
    unitPriceTetri: number;
    performedByAdminId: string | null;
  }>;
  discountBp: number;
  requestedRedemptionTetri: number;
  notes: string | null;
}

export const useDrafts = () =>
  useQuery({
    queryKey: ['drafts'],
    queryFn: () => api<{ items: DraftSummary[] }>('/admin/orders/drafts'),
  });

export const useDraft = (id: string | null) =>
  useQuery({
    queryKey: ['draft', id],
    queryFn: () => api<DraftDetail>(`/admin/orders/drafts/${id}`),
    enabled: id !== null,
  });

export function useSaveDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: string | null }) =>
      api<{ id: string }>(id ? `/admin/orders/drafts/${id}` : '/admin/orders/drafts', {
        method: id ? 'PUT' : 'POST',
        body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['drafts'] }),
  });
}

export function useDiscardDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ discarded: boolean }>(`/admin/orders/drafts/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['drafts'] }),
  });
}

export const useResolveQr = () =>
  useMutation({
    mutationFn: (token: string) =>
      api<QrResolution>('/admin/qr/resolve', { method: 'POST', body: { token } }),
  });

export function useBlockCustomer(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { block: boolean; reason?: string }) =>
      api<{ ok: true }>(`/admin/customers/${customerId}/${input.block ? 'block' : 'unblock'}`, {
        method: 'POST',
        ...(input.block ? { body: { reason: input.reason } } : {}),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer', customerId] }),
  });
}

/**
 * Permanent deletion, owner only.
 *
 * The whole customer cache is dropped rather than one key: lists, reports and
 * the vehicle register all showed this person a moment ago.
 */
export function useDeleteCustomer(customerId: string, onDeleted: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (confirm: string) =>
      api<{ ok: true; loyaltyId: string; removed: Record<string, number> }>(
        `/admin/customers/${customerId}`,
        { method: 'DELETE', body: { confirm } },
      ),
    /*
     * Leave the page before the cache is refreshed, not after.
     *
     * Invalidating first refetches a customer who no longer exists, the page
     * flips to "could not load", and the button that started this unmounts —
     * taking its own onSuccess with it. This runs either way.
     */
    onSuccess: () => {
      onDeleted();
      queryClient.removeQueries({ queryKey: ['customer', customerId] });
      void queryClient.invalidateQueries();
    },
  });
}

export function useOrderList(search: string, status?: string) {
  const params = new URLSearchParams();
  if (search.trim()) params.set('q', search.trim());
  if (status) params.set('status', status);

  return useQuery({
    queryKey: ['orders', search.trim(), status ?? ''],
    queryFn: () => api<{ items: OrderListItem[]; nextCursor: string | null }>(
      `/admin/orders?${params.toString()}`,
    ),
    placeholderData: keepPreviousData,
  });
}

export const useOrder = (id: string | undefined) =>
  useQuery({
    queryKey: ['order', id],
    enabled: Boolean(id),
    queryFn: () => api<OrderDetail>(`/admin/orders/${id}`),
  });

export function useRefundOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string; items?: Array<{ orderItemId: string; quantity: number }> }) =>
      api<RefundResult>(`/admin/orders/${orderId}/refund`, {
        method: 'POST',
        body,
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['customer'] });
    },
  });
}

/** Public endpoint, read here so the staff screen can explain how the customer signs in. */
export const useAuthMethods = () =>
  useQuery({
    queryKey: ['customer-auth-methods'],
    staleTime: 5 * 60_000,
    queryFn: () => api<{ otp: { available: boolean } }>('/customer/auth/methods'),
  });

export const useSettings = () =>
  useQuery({
    queryKey: ['settings'],
    queryFn: () => api<{ items: SettingRow[]; rulesVersion: number }>('/admin/settings'),
  });

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ changed: string[]; rulesVersion: number | null }>('/admin/settings', {
        method: 'PATCH', body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  });
}

export const useTiers = () =>
  useQuery({
    queryKey: ['tiers'],
    queryFn: () => api<{ items: TierRow[] }>('/admin/loyalty/tiers'),
  });

export function useUpdateTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<TierRow>(`/admin/loyalty/tiers/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiers'] });
      queryClient.invalidateQueries({ queryKey: ['meta'] });
    },
  });
}

export const useAudit = (action: string) =>
  useQuery({
    queryKey: ['audit', action],
    queryFn: () => api<{ items: AuditRow[]; nextCursor: string | null }>(
      `/admin/audit${action ? `?action=${encodeURIComponent(action)}` : ''}`,
    ),
    placeholderData: keepPreviousData,
  });

export const useDashboard = () =>
  useQuery({
    queryKey: ['dashboard'],
    staleTime: 30_000,
    queryFn: () => api<DashboardData>('/admin/dashboard'),
  });

export function useAdjustment(customerId: string, kind: 'cashback' | 'points') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { amount: number; reason: string }) =>
      api<{ balanceAfterTetri?: number; balanceAfter?: number }>(
        `/admin/customers/${customerId}/loyalty/${kind}-adjustment`,
        { method: 'POST', body, idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useLegacyImport(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { lifetimeSpendTetri: number; reason: string }) =>
      api<{ lifetimeSpendTetri: number; tierCode: string; tierChanged: boolean }>(
        `/admin/customers/${customerId}/loyalty/legacy-import`,
        { method: 'POST', body, idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer', customerId] }),
  });
}

export interface ImportBatch {
  id: string;
  filename: string;
  status: string;
  totalRows: number;
  validRows: number;
  duplicateRows: number;
  invalidRows: number;
  importedRows: number;
  failedRows: number;
  createdAt: string;
}

export interface ImportRow {
  id: string;
  rowNumber: number;
  status: string;
  messages: string[];
  rawData: Record<string, unknown>;
  createdCustomerId: string | null;
  matchedCustomerId: string | null;
}

export const useImports = () =>
  useQuery({
    queryKey: ['imports'],
    queryFn: () => api<{ items: ImportBatch[] }>('/admin/imports'),
  });

export const useImport = (id: string | null) =>
  useQuery({
    queryKey: ['import', id],
    enabled: Boolean(id),
    queryFn: () => api<{
      batch: ImportBatch;
      rows: ImportRow[];
      progress: { processed: number; total: number; percent: number };
    }>(`/admin/imports/${id}`),
    // Polls only while the worker is still writing rows.
    refetchInterval: (query) => {
      const status = query.state.data?.batch.status;
      return status === 'CONFIRMED' || status === 'PROCESSING' ? 1_500 : false;
    },
  });

export function useUploadImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { filename: string; content: string }) =>
      api<ImportBatch>('/admin/imports', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['imports'] }),
  });
}

export function useConfirmImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true; queued: number }>(`/admin/imports/${id}/confirm`, { method: 'POST' }),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['import', id] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export const useStaff = (includeInactive = false) =>
  useQuery({
    queryKey: ['staff', includeInactive],
    queryFn: () => api<{ items: StaffMember[] }>(
      `/admin/staff${includeInactive ? '?includeInactive=true' : ''}`,
    ),
  });

export const useRoles = () =>
  useQuery({ queryKey: ['roles'], queryFn: () => api<{ items: RoleRow[] }>('/admin/roles') });

export const usePermissions = () =>
  useQuery({
    queryKey: ['permissions'],
    staleTime: 10 * 60_000,
    queryFn: () => api<{ groups: PermissionGroup[] }>('/admin/permissions'),
  });

function invalidateStaff(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['staff'] });
  queryClient.invalidateQueries({ queryKey: ['roles'] });
}

export function useCreateStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ id: string; email: string }>('/admin/staff', { method: 'POST', body }),
    onSuccess: () => invalidateStaff(queryClient),
  });
}

export function useUpdateStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<{ ok: true }>(`/admin/staff/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateStaff(queryClient),
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<RoleRow>('/admin/roles', { method: 'POST', body }),
    onSuccess: () => invalidateStaff(queryClient),
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<{ ok: true }>(`/admin/roles/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateStaff(queryClient),
  });
}

export function useArchiveRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/admin/roles/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateStaff(queryClient),
  });
}

export interface AdminReward {
  id: string;
  name: Localized;
  description: Localized | null;
  pointsCost: number;
  validityDays: number | null;
  redemptionLimitPerCustomer: number | null;
  totalRedemptionLimit: number | null;
  isActive: boolean;
  sortOrder: number;
  issued: number;
}

export interface RedemptionRow {
  id: string;
  code: string;
  status: string;
  pointsSpent: number;
  rewardNameSnapshot: Localized;
  expiresAt: string | null;
  usedAt: string | null;
  createdAt: string;
  customerId: string;
  customerName: string;
  loyaltyId: string;
}

export const useAdminRewards = () =>
  useQuery({ queryKey: ['admin-rewards'], queryFn: () => api<{ items: AdminReward[] }>('/admin/rewards') });

export const useRedemptions = (status?: string) =>
  useQuery({
    queryKey: ['redemptions', status ?? ''],
    queryFn: () => api<{ items: RedemptionRow[] }>(
      `/admin/rewards/redemptions${status ? `?status=${status}` : ''}`,
    ),
    placeholderData: keepPreviousData,
  });

function invalidateRewards(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['admin-rewards'] });
  queryClient.invalidateQueries({ queryKey: ['redemptions'] });
}

export function useSaveReward() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      api<AdminReward>(id ? `/admin/rewards/${id}` : '/admin/rewards', {
        method: id ? 'PATCH' : 'POST', body,
      }),
    onSuccess: () => invalidateRewards(queryClient),
  });
}

export function useArchiveReward() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/admin/rewards/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateRewards(queryClient),
  });
}

export function useUseRedemption() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api<{ code: string; reward: Localized; customer: { id: string; name: string; loyaltyId: string } }>(
        '/admin/rewards/redemptions/use', { method: 'POST', body: { code } },
      ),
    onSuccess: () => invalidateRewards(queryClient),
  });
}

export interface ReportSummary {
  range: { from: string; to: string };
  totals: {
    orders: number; customers: number; revenueTetri: number; discountTetri: number;
    cashbackUsedTetri: number; cashbackEarnedTetri: number; pointsEarned: number;
    averageOrderTetri: number;
  };
  topServices: Array<{ name: Localized; quantity: number; revenueTetri: number }>;
  topCustomers: Array<{ id: string; name: string; orders: number; revenueTetri: number }>;
}

export const useReport = (from: string, to: string) =>
  useQuery({
    queryKey: ['report', from, to],
    queryFn: () => api<ReportSummary>(`/admin/reports/summary?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
  });

export function useUpdateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<Service>(`/admin/services/${id}`, { method: 'PATCH', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['services'] }),
  });
}

export interface MergePreview {
  source: MergeSide;
  target: MergeSide;
  result: {
    lifetimeSpendTetri: number; cashbackAvailableTetri: number; cashbackPendingTetri: number;
    pointsBalance: number; totalOrders: number; tierCode: string;
    tierHeldByPermanence: boolean; vehicles: number;
  };
}

export interface MergeSide {
  customer: {
    id: string; loyaltyId: string; firstName: string; lastName: string | null;
    phone: string; status: string; createdAt: string;
  };
  loyalty: {
    lifetimeSpendTetri: number; cashbackAvailableTetri: number;
    cashbackPendingTetri: number; pointsBalance: number; totalOrders: number;
  };
  counts: { vehicles: number; orders: number; redemptions: number; notes: number };
}

export const useMergePreview = (sourceId: string | null, targetId: string | null) =>
  useQuery({
    queryKey: ['merge-preview', sourceId, targetId],
    enabled: Boolean(sourceId && targetId && sourceId !== targetId),
    queryFn: () => api<MergePreview>(
      `/admin/customers/merge/preview?sourceCustomerId=${sourceId}&targetCustomerId=${targetId}`,
    ),
  });

export function useMergeCustomers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { sourceCustomerId: string; targetCustomerId: string; reason: string }) =>
      api<{
        sourceLoyaltyId: string; targetLoyaltyId: string; tierCode: string;
        tierChanged: boolean; lifetimeSpendTetri: number;
        moved: { spendMoved: number; cashbackMoved: number; pointsMoved: number };
      }>('/admin/customers/merge', { method: 'POST', body, idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer'] });
    },
  });
}

/*
 * The floor: cars that are here and not yet paid for.
 *
 * Polled rather than pushed. A board on the wall that is thirty seconds stale
 * is still a board; a websocket that silently dies is a board nobody trusts.
 */
export const useFloor = () =>
  useQuery({
    queryKey: ['floor'],
    queryFn: () => api<{ items: FloorCar[] }>('/admin/floor'),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });

export const useFloorCar = (id: string | undefined) =>
  useQuery({
    queryKey: ['floor', id],
    enabled: Boolean(id),
    queryFn: () => api<FloorDetail>(`/admin/floor/${id}`),
  });

export const useMyWork = () =>
  useQuery({
    queryKey: ['my-work'],
    queryFn: () => api<{ items: MyJob[] }>('/admin/floor/mine'),
    refetchInterval: 30_000,
  });

/** Just the number for the badge, so the sidebar can poll it cheaply. */
export const useMyWorkCount = (enabled: boolean) =>
  useQuery({
    queryKey: ['my-work', 'count'],
    enabled,
    queryFn: () => api<{ waiting: number }>('/admin/floor/mine/count'),
    refetchInterval: 60_000,
  });

/** Names for the assignment menu, without the staff directory's permissions. */
export const useTechnicians = (enabled: boolean) =>
  useQuery({
    queryKey: ['technicians'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: () => api<{
      items: Array<{ id: string; firstName: string; lastName: string; jobTitle: string | null; isOwner: boolean }>;
    }>('/admin/floor/technicians'),
  });

/**
 * The people the signed-in member of staff may hand work to: an owner only by
 * another owner. Whoever already holds the job stays listed, so the menu shows them.
 */
export function useAssignees(enabled: boolean) {
  const { data: staff } = useTechnicians(enabled);
  const { data: session } = useSession();
  const owner = session?.roles.includes('OWNER') ?? false;
  return (current?: string | null) =>
    (staff?.items ?? []).filter((person) => owner || !person.isOwner || person.id === current);
}

/** The same visit again: what a regular had done last time. */
export interface LastOrder {
  order: { id: string; reference: string; completedAt: string | null; vehicleId: string | null } | null;
  lines: Array<{
    serviceId: string | null;
    name: Localized;
    quantity: number;
    unitPriceTetri: number;
    performedByAdminId: string | null;
  }>;
}

export const useLastOrder = (customerId: string | undefined) =>
  useQuery({
    queryKey: ['customer', customerId, 'last-order'],
    enabled: Boolean(customerId),
    queryFn: () => api<LastOrder>(`/admin/customers/${customerId}/last-order`),
  });

export interface AcceptCarInput {
  customerId: string;
  vehicleId: string | null;
  promisedAt: string | null;
  bookingId?: string | null;
  /** The walk-round photographed at the door. */
  inspectionId?: string | null;
  /** Taken in now, photographed later. */
  photosLater?: boolean;
  notes?: string;
  /** `serviceId` null means a one-off agreed at the door, named in `customName`. */
  lines: Array<{
    serviceId: string | null;
    customName: string | null;
    quantity: number;
    performedByAdminId: string | null;
  }>;
}

export function useAcceptCar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AcceptCarInput) =>
      api<{ id: string; reference: string; status: string }>('/admin/floor/accept', {
        method: 'POST', body, idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['floor'] }),
  });
}

export function useSetWork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, status }: { itemId: string; status: WorkStatus }) =>
      api<{ orderStatus: FloorStatus }>(`/admin/floor/items/${itemId}/work`, {
        method: 'PATCH', body: { status },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor'] });
      queryClient.invalidateQueries({ queryKey: ['my-work'] });
    },
  });
}

export function useAssignWork(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { itemId: string; performedByAdminId: string | null }) =>
      api<{ ok: true }>(`/admin/floor/${orderId}/assign`, { method: 'PATCH', body: input }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor'] });
      queryClient.invalidateQueries({ queryKey: ['my-work'] });
    },
  });
}

export function useSetPromised(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (promisedAt: string | null) =>
      api<{ ok: true }>(`/admin/floor/${orderId}/promised`, { method: 'PATCH', body: { promisedAt } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor'] });
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
  });
}

export function useSetFloorVehicle(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vehicleId: string) =>
      api<{ ok: true }>(`/admin/floor/${orderId}/vehicle`, { method: 'PATCH', body: { vehicleId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['floor'] }),
  });
}

export function useAddWork(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lines: AcceptCarInput['lines']) =>
      api<{ id: string; reference: string; status: string }>(`/admin/floor/${orderId}/lines`, {
        method: 'POST', body: { lines }, idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor'] });
      queryClient.invalidateQueries({ queryKey: ['my-work'] });
    },
  });
}

export function useCancelCar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<{ ok: true; reference: string }>(`/admin/floor/${id}/cancel`, {
        method: 'POST', body: { reason }, idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['floor'] }),
  });
}

export const useBookings = (scope: 'open' | 'today' | 'history' = 'open') =>
  useQuery({
    queryKey: ['bookings', scope],
    queryFn: () => api<{ items: Booking[] }>(`/admin/bookings?scope=${scope}`),
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });

export const useBookingCount = (enabled: boolean) =>
  useQuery({
    queryKey: ['bookings', 'count'],
    enabled,
    queryFn: () => api<{ today: number }>('/admin/bookings/count'),
    refetchInterval: 120_000,
  });

export interface BookingInput {
  name: string;
  phone?: string;
  customerId?: string | null;
  vehicleNote?: string;
  request?: string;
  note?: string;
  expectedAt: string;
  durationMinutes?: number;
  assignedAdminId?: string | null;
}

export function useSaveBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: BookingInput & { id?: string }) =>
      id
        ? api<Booking>(`/admin/bookings/${id}`, { method: 'PATCH', body })
        : api<Booking>('/admin/bookings', {
            method: 'POST', body, idempotencyKey: newIdempotencyKey(),
          }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
  });
}

export function useCloseBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, how, reason }: { id: string; how: 'cancel' | 'no-show'; reason?: string }) =>
      api<{ ok: true }>(`/admin/bookings/${id}/${how}`, {
        method: 'POST', body: { reason }, idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
  });
}

export interface ServiceCategory {
  id: string;
  slug: string;
  name: Localized;
  isActive: boolean;
}

export const useServiceCategories = () =>
  useQuery({
    queryKey: ['service-categories'],
    queryFn: () => api<{ items: ServiceCategory[] }>('/admin/service-categories'),
  });

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: Localized }) =>
      api<ServiceCategory>('/admin/service-categories', {
        method: 'POST', body, idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['service-categories'] }),
  });
}

export interface NewServiceInput {
  categoryId: string;
  name: Localized;
  priceFromTetri: number | null;
  followupDays: number | null;
  /** Sent only by someone who may set pay. */
  staffPayTetri?: number | null;
}

export function useCreateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NewServiceInput) =>
      api<Service>('/admin/services', {
        method: 'POST', body, idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['services'] }),
  });
}

/** Who earned what over a period, net of refunds. */
export interface Earner {
  adminUserId: string;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  isActive: boolean;
  jobs: number;
  cars: number;
  valueTetri: number;
  refundedTetri: number;
  /** Null unless this person may see pay. */
  payTetri: number | null;
}

export const useStaffEarnings = (from: string, to: string) =>
  useQuery({
    queryKey: ['staff-earnings', from, to],
    queryFn: () => api<{
      from: string; to: string;
      items: Earner[];
      totals: {
        people: number; jobs: number; valueTetri: number; refundedTetri: number; payTetri: number | null;
      };
    }>(`/admin/reports/staff-earnings?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
  });

export function useSetStaffPay(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, staffPayTetri }: { itemId: string; staffPayTetri: number | null }) =>
      api<{ ok: true }>(`/admin/orders/${orderId}/items/${itemId}/pay`, {
        method: 'PATCH', body: { staffPayTetri },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['staff-earnings'] });
      queryClient.invalidateQueries({ queryKey: ['payroll'] });
    },
  });
}

export function useTakePayment(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { method: string; amountTetri: number }) =>
      api<{ reference: string; paidTetri: number; outstandingTetri: number }>(
        `/admin/orders/${orderId}/payments`,
        { method: 'POST', body, idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/* ---- Inspections ---------------------------------------------------------- */

export const useInspectionRules = () =>
  useQuery({
    queryKey: ['inspection-rules'],
    staleTime: 5 * 60_000,
    queryFn: () => api<{ intakeRequired: boolean; handoverRequired: boolean }>('/admin/inspections/rules'),
  });

/** Starts a walk-round, or picks up the one already open for this car. */
export function useStartInspection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: 'INTAKE'; vehicleId: string } | { kind: 'INTAKE' | 'HANDOVER'; orderId: string }) =>
      api<Inspection>('/admin/inspections', { method: 'POST', body, idempotencyKey: newIdempotencyKey() }),
    onSuccess: (inspection) => queryClient.setQueryData(['inspection', inspection.id], inspection),
  });
}

export const useInspection = (id: string | null) =>
  useQuery({
    queryKey: ['inspection', id],
    enabled: Boolean(id),
    queryFn: () => api<Inspection>(`/admin/inspections/${id}`),
  });

export interface PhotoUpload {
  slot: InspectionPhoto['slot'];
  damageType?: InspectionPhoto['damageType'];
  part?: InspectionPhoto['part'];
  note?: string | null;
  image: string;
  thumb: string;
  width: number;
  height: number;
}

export function useUploadPhoto(inspectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // One key per photo, kept across retries, so a shaky connection never stores it twice.
    mutationFn: ({ key, ...body }: PhotoUpload & { key: string }) =>
      api<InspectionPhoto>(`/admin/inspections/${inspectionId}/photos`, {
        method: 'POST', body, idempotencyKey: key,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] }),
  });
}

export function useDeletePhoto(inspectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (photoId: string) =>
      api<{ ok: true }>(`/admin/inspections/${inspectionId}/photos/${photoId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] }),
  });
}

export function usePatchInspection(inspectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { odometerKm?: number | null; notes?: string | null }) =>
      api<Inspection>(`/admin/inspections/${inspectionId}`, { method: 'PATCH', body }),
    onSuccess: (inspection) => queryClient.setQueryData(['inspection', inspectionId], inspection),
  });
}

export function useCompleteInspection(inspectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<Inspection>(`/admin/inspections/${inspectionId}/complete`, {
        method: 'POST', body: {}, idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (inspection) => {
      queryClient.setQueryData(['inspection', inspectionId], inspection);
      queryClient.invalidateQueries({ queryKey: ['floor'] });
      queryClient.invalidateQueries({ queryKey: ['order-inspections'] });
    },
  });
}

export const useOrderInspections = (orderId: string | undefined) =>
  useQuery({
    queryKey: ['order-inspections', orderId],
    enabled: Boolean(orderId),
    queryFn: () => api<{ items: Inspection[] }>(`/admin/orders/${orderId}/inspections`),
  });

export const useVehicleHistory = (vehicleId: string | undefined) =>
  useQuery({
    queryKey: ['vehicle-history', vehicleId],
    enabled: Boolean(vehicleId),
    queryFn: () => api<VehicleHistory>(`/admin/vehicles/${vehicleId}/history`),
  });


/** The calendar for whatever range is on screen; `staffId` narrows it to one person. */
/** Moves or stretches a person's block of work on a car; null puts it back to what the work recorded. */
export function useSetJobTime() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, staffId, time }: {
      orderId: string; staffId: string; time: { start: string; end: string } | null;
    }) => api<unknown>(`/admin/calendar/jobs/${orderId}/${staffId}`, time
      ? { method: 'PUT', body: time }
      : { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['calendar'] }),
  });
}

/** A booking dragged longer or shorter on the grid. */
export function useResizeBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, durationMinutes }: { id: string; durationMinutes: number }) =>
      api<Booking>(`/admin/bookings/${id}`, { method: 'PATCH', body: { durationMinutes } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
  });
}

export const useCalendar = (range: { from: string; to: string } | null, staffId: string | null) =>
  useQuery({
    queryKey: ['calendar', range?.from, range?.to, staffId],
    enabled: Boolean(range),
    placeholderData: keepPreviousData,
    // A technician's blocks grow while they work; a minute is fresh enough to glance at.
    refetchInterval: 60_000,
    queryFn: () => {
      const params = new URLSearchParams({ from: range!.from, to: range!.to });
      if (staffId) params.set('staffId', staffId);
      return api<CalendarData>(`/admin/calendar?${params}`);
    },
  });

export interface PayrollPerson {
  adminUserId: string;
  name: string;
  jobTitle: string | null;
  isActive: boolean;
  todayTetri: number;
  weekTetri: number;
  monthTetri: number;
  periodTetri: number;
  jobs: number;
  cars: number;
  /** Jobs nobody has set the pay for yet. */
  unpriced: number;
}

export const usePayroll = (from: string, to: string) =>
  useQuery({
    queryKey: ['payroll', from, to],
    queryFn: () => api<{
      people: PayrollPerson[];
      totals: { todayTetri: number; weekTetri: number; monthTetri: number; periodTetri: number; jobs: number; unpriced: number };
    }>(`/admin/payroll?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
  });

export interface PayrollJob {
  itemId: string;
  orderId: string;
  reference: string;
  completedAt: string;
  service: Localized;
  quantity: number;
  refundedQuantity: number;
  payTetri: number | null;
  car: string | null;
}

export const usePayrollJobs = (adminUserId: string | null, from: string, to: string) =>
  useQuery({
    queryKey: ['payroll', adminUserId, from, to],
    enabled: adminUserId !== null,
    queryFn: () => api<{ items: PayrollJob[] }>(`/admin/payroll/${adminUserId}/jobs?from=${from}&to=${to}`),
  });
